import { useState, useMemo, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Search, RefreshCw, AlertCircle, RotateCcw, Trash2 } from "lucide-react"
import { Input, Button, Card, CardBody, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Chip, Spinner, Tooltip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react"
import { useListTasksPaginated, useUpdateTaskStatus, useDeleteTask } from "@src/presentation/hooks/task"
import type { TaskListItemDTO } from "@src/application/tasks/dto"
import { formatDateHuman } from "@src/shared/utils/date-formatter"
import { ToastService } from "@src/shared/services/toast.service"
import { useInView } from "react-intersection-observer"

export default function FailedTasksPage() {
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState("")
    const [rescheduleConfirmOpen, setRescheduleConfirmOpen] = useState(false)
    const [rescheduleAllConfirmOpen, setRescheduleAllConfirmOpen] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [selectedTask, setSelectedTask] = useState<TaskListItemDTO | null>(null)

    // Intersection observer for infinite scroll
    const { ref, inView } = useInView();

    // Fetch failed tasks with pagination
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error, refetch } = useListTasksPaginated({
        limit: 20,
        status: "failed"
    });
    const { mutate: updateStatus, isPending: isUpdating } = useUpdateTaskStatus({
        onSuccess: () => {
            ToastService.success("Task rescheduled successfully")
            setRescheduleConfirmOpen(false)
            setRescheduleAllConfirmOpen(false)
            refetch()
        },
        onError: (error) => {
            ToastService.error(`Failed to reschedule task: ${error.message}`)
        }
    })
    const { mutate: deleteTask, isPending: isDeleting } = useDeleteTask({
        onSuccess: () => {
            ToastService.success("Task deleted successfully")
            setDeleteConfirmOpen(false)
            refetch()
        },
        onError: (error) => {
            ToastService.error(`Failed to delete task: ${error.message}`)
        }
    })

    // Flatten the paginated data
    const tasks = data?.pages.flatMap((page: any) => page?.tasks || []) || [];

    // Load more when in view
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Filter tasks based on search (status filtering is already done server-side)
    const failedTasks = useMemo(() => {
        if (!Array.isArray(tasks)) {
            return [];
        }
        return tasks.filter((task: TaskListItemDTO) => {
            if (searchQuery === "") return true
            return (
                task.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                task.pascoId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                task.errorMessage?.toLowerCase().includes(searchQuery.toLowerCase())
            )
        })
    }, [tasks, searchQuery])

    const handleReschedule = (task: TaskListItemDTO) => {
        setSelectedTask(task)
        setRescheduleConfirmOpen(true)
    }

    const confirmReschedule = () => {
        if (selectedTask) {
            updateStatus({
                taskId: selectedTask.id,
                updateData: { status: "pending" }
            })
        }
    }

    const handleRescheduleAll = () => {
        setRescheduleAllConfirmOpen(true)
    }

    const confirmRescheduleAll = () => {
        failedTasks.forEach((task) => {
            updateStatus({
                taskId: task.id,
                updateData: { status: "pending" }
            })
        })
    }

    const handleDelete = (task: TaskListItemDTO) => {
        setSelectedTask(task)
        setDeleteConfirmOpen(true)
    }

    const confirmDelete = () => {
        if (selectedTask) {
            deleteTask(selectedTask.id)
        }
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-danger">
                                <AlertCircle className="h-5 w-5 text-danger-foreground" />
                            </div>
                            <h1 className="text-xl font-semibold text-foreground">Failed Tasks</h1>
                            <span className="rounded-full bg-danger/10 px-2.5 py-0.5 text-xs font-medium text-danger">
                                {failedTasks.length} {failedTasks.length === 1 ? "task" : "tasks"}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            {failedTasks.length > 0 && (
                                <Button
                                    color="warning"
                                    onPress={handleRescheduleAll}
                                    isLoading={isUpdating}
                                    startContent={<RotateCcw className="h-4 w-4" />}
                                    className="rounded flex-1 sm:flex-none"
                                    size="sm"
                                >
                                    <span className="hidden sm:inline">Reschedule All</span>
                                    <span className="sm:hidden">Reschedule All</span>
                                </Button>
                            )}
                            <Button
                                color="default"
                                variant="flat"
                                onPress={() => refetch()}
                                isLoading={isLoading}
                                startContent={<RefreshCw className="h-4 w-4" />}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Refresh</span>
                                <span className="sm:hidden">Refresh</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6">
                    {/* Info Alert */}
                    <Card className="border-l-4 border-l-danger shadow-none border border-border rounded">
                        <CardBody className="p-4">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-danger mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="font-semibold text-foreground">Failed Tasks</h3>
                                    <p className="text-sm text-default-600 mt-1">
                                        These tasks have failed during processing. You can reschedule them to retry or delete them if they are no longer needed.
                                        Review the error messages to understand why the task failed.
                                    </p>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Search */}
                    <Card className="shadow-none border border-border rounded">
                        <CardBody className="p-4">
                            <Input
                                type="text"
                                placeholder="Search by task ID, PASCO ID, or error message..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                startContent={<Search className="h-4 w-4 text-default-400" />}
                                classNames={{
                                    input: "text-sm",
                                    inputWrapper: "h-10",
                                }}
                            />
                        </CardBody>
                    </Card>

                    {/* Failed Tasks Table */}
                    <Card className="shadow-none border border-border rounded">
                        <CardBody className="p-0">
                            {isLoading ? (
                                <div className="flex items-center justify-center p-8">
                                    <Spinner size="lg" />
                                </div>
                            ) : error ? (
                                <div className="flex flex-col items-center justify-center p-8 text-danger">
                                    <AlertCircle className="h-12 w-12 mb-2" />
                                    <p>Error loading tasks: {error.message}</p>
                                </div>
                            ) : failedTasks.length === 0 ? (
                                <div className="flex flex-col items-center justify-center p-8 text-default-500">
                                    <AlertCircle className="h-12 w-12 mb-2" />
                                    <p className="font-semibold">No failed tasks</p>
                                    <p className="text-sm mt-1">All tasks are running smoothly!</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <Table aria-label="Failed tasks table">
                                        <TableHeader>
                                            <TableColumn>TASK ID</TableColumn>
                                            <TableColumn className="hidden lg:table-cell">PASCO ID</TableColumn>
                                            <TableColumn className="hidden lg:table-cell">ATTEMPTS</TableColumn>
                                            <TableColumn className="hidden md:table-cell">ERROR MESSAGE</TableColumn>
                                            <TableColumn className="hidden md:table-cell">FAILED AT</TableColumn>
                                            <TableColumn>ACTIONS</TableColumn>
                                        </TableHeader>
                                        <TableBody>
                                            {failedTasks.map((task: TaskListItemDTO) => (
                                                <TableRow key={task.id} className="min-h-[64px]">
                                                    <TableCell>
                                                        <Tooltip content={task.id}>
                                                            <code className="text-xs cursor-pointer">
                                                                {task.id.substring(0, 8)}...
                                                            </code>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell className="hidden lg:table-cell">
                                                        <Button
                                                            variant="light"
                                                            size="sm"
                                                            onPress={() => navigate(`/pascos/${task.pascoId}`)}
                                                            className="text-xs"
                                                        >
                                                            {task.pascoId.substring(0, 8)}...
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell className="hidden lg:table-cell">
                                                        <Chip
                                                            size="sm"
                                                            variant="flat"
                                                            color={task.attemptCount > 3 ? "danger" : "warning"}
                                                        >
                                                            {task.attemptCount} {task.attemptCount === 1 ? "attempt" : "attempts"}
                                                        </Chip>
                                                    </TableCell>
                                                    <TableCell className="hidden md:table-cell">
                                                        {task.errorMessage ? (
                                                            <Tooltip content={task.errorMessage}>
                                                                <span className="text-xs text-danger cursor-pointer line-clamp-2">
                                                                    {task.errorMessage}
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            <span className="text-xs text-default-400">No error message</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="hidden md:table-cell">
                                                        <span className="text-xs">{formatDateHuman(task.updatedAt)}</span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="flat"
                                                                onPress={() => navigate(`/tasks/${task.id}`)}
                                                            >
                                                                View
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                color="warning"
                                                                variant="flat"
                                                                onPress={() => handleReschedule(task)}
                                                                isLoading={isUpdating}
                                                                startContent={<RotateCcw className="h-3 w-3" />}
                                                            >
                                                                Reschedule
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                color="danger"
                                                                variant="flat"
                                                                onPress={() => handleDelete(task)}
                                                                isLoading={isDeleting}
                                                                startContent={<Trash2 className="h-3 w-3" />}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                            {/* Infinite scroll trigger */}
                            {hasNextPage && (
                                <div ref={ref} className="h-4" />
                            )}

                            {/* Loading spinner when fetching next page */}
                            {isFetchingNextPage && (
                                <div className="mt-4">
                                    <Card className="border border-border rounded shadow-none">
                                        <CardBody className="p-4">
                                            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                                                Loading more failed tasks...
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </main>

            {/* Reschedule Confirmation Modal */}
            <Modal
                isOpen={rescheduleConfirmOpen}
                onClose={() => setRescheduleConfirmOpen(false)}
                classNames={{
                    base: "rounded border border-border",
                    backdrop: "bg-black/50"
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <RotateCcw className="h-5 w-5 text-warning" />
                            <span>Reschedule Task</span>
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <p className="text-sm text-default-600">
                            Are you sure you want to reschedule this task? It will be moved to pending status and retried.
                        </p>
                        {selectedTask && (
                            <div className="mt-4 p-3 bg-default-100 rounded border border-border">
                                <div className="space-y-2 text-sm">
                                    <div>
                                        <span className="text-default-500">Task ID: </span>
                                        <code className="text-xs">{selectedTask.id.substring(0, 16)}...</code>
                                    </div>
                                    <div>
                                        <span className="text-default-500">Attempts: </span>
                                        <span className="font-medium">{selectedTask.attemptCount}</span>
                                    </div>
                                    {selectedTask.errorMessage && (
                                        <div>
                                            <span className="text-default-500">Error: </span>
                                            <span className="text-danger text-xs">{selectedTask.errorMessage}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="flat"
                            onPress={() => setRescheduleConfirmOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="warning"
                            onPress={confirmReschedule}
                            isLoading={isUpdating}
                        >
                            Reschedule
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Reschedule All Confirmation Modal */}
            <Modal
                isOpen={rescheduleAllConfirmOpen}
                onClose={() => setRescheduleAllConfirmOpen(false)}
                classNames={{
                    base: "rounded border border-border",
                    backdrop: "bg-black/50"
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <RotateCcw className="h-5 w-5 text-warning" />
                            <span>Reschedule All Failed Tasks</span>
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <p className="text-sm text-default-600">
                            Are you sure you want to reschedule all <span className="font-semibold text-warning">{failedTasks.length}</span> failed tasks?
                            They will all be moved to pending status and retried.
                        </p>
                        <div className="mt-4 p-3 bg-warning/10 rounded border border-warning/20">
                            <p className="text-sm text-warning-600">
                                ⚠️ This action will reschedule all failed tasks. Make sure this is what you want to do.
                            </p>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="flat"
                            onPress={() => setRescheduleAllConfirmOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="warning"
                            onPress={confirmRescheduleAll}
                            isLoading={isUpdating}
                        >
                            Reschedule All
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                classNames={{
                    base: "rounded border border-border",
                    backdrop: "bg-black/50"
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-danger" />
                            <span>Delete Task</span>
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <p className="text-sm text-default-600">
                            Are you sure you want to delete this task? This action cannot be undone.
                        </p>
                        {selectedTask && (
                            <div className="mt-4 p-3 bg-danger/10 rounded border border-danger/20">
                                <div className="space-y-2 text-sm">
                                    <div>
                                        <span className="text-default-500">Task ID: </span>
                                        <code className="text-xs">{selectedTask.id.substring(0, 16)}...</code>
                                    </div>
                                    <div>
                                        <span className="text-default-500">PASCO ID: </span>
                                        <code className="text-xs">{selectedTask.pascoId.substring(0, 16)}...</code>
                                    </div>
                                </div>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="flat"
                            onPress={() => setDeleteConfirmOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            onPress={confirmDelete}
                            isLoading={isDeleting}
                        >
                            Delete
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    )
}
